import { Button, CheckBox, InputRadio, InputTextArea } from 'components/atoms'
import { Form } from 'components/molecules'
import { reservationService } from 'core/services'
import * as React from 'react'
import 'react-dates/initialize'
import I18n from '../../../core/i18n'
import * as interfaces from '../../../static/interfaces'
import SvgIcon from 'components/atoms/SvgIcon'
import styled from 'styled-components'

declare var window: interfaces.IWindow
interface IReservationCancelModalBodyProps {
  reservationId: number
  reservationStatus: string
}

const OTHER_REASON_VALUE = 'other'
const OTHER_REASON_MIN_LENGTH = 15
const otherReasonTooShortError = () =>
  I18n.t('errors.format', {
    attribute: I18n.t('cancel.cancel_reason'),
    message: I18n.t('errors.messages.too_short', { count: OTHER_REASON_MIN_LENGTH }),
  })
const CANCEL_REASON_OPTIONS = [
  { id: 'change-date', value: 'Decided to change the date', label: 'Decided to change the date' },
  {
    id: 'double-booking',
    value: 'Double booking / Duplicate reservation',
    label: 'Double booking / Duplicate reservation',
  },
  { id: 'change-plans', value: 'Change of personal plans', label: 'Change of personal plans' },
  { id: 'booked-by-mistake', value: 'Booked by mistake', label: 'Booked by mistake' },
  {
    id: 'transportation-issues',
    value: 'Transportation issues (Flight canceled, etc.)',
    label: 'Transportation issues (Flight canceled, etc.)',
  },
  {
    id: 'health-reasons',
    value: 'Health reasons / Sudden illness',
    label: 'Health reasons / Sudden illness',
  },
  { id: 'other', value: OTHER_REASON_VALUE, label: 'Other (Please specify below)' },
]

const ReservationCancelModalBody: React.FC<IReservationCancelModalBodyProps> = props => {
  const [price, setPrice] = React.useState<number | null>(null)
  const [selectedReason, setSelectedReason] = React.useState<string>(CANCEL_REASON_OPTIONS[0].value)
  const [isCancellationFeeConfirmed, setIsCancellationFeeConfirmed] = React.useState<boolean>(false)
  const [otherReason, setOtherReason] = React.useState<string>('')

  const hasCancellationFee = price === null ? null : price > 0
  const trimmedOtherReason = otherReason.trim()
  const isOtherReasonTooShort =
    selectedReason === OTHER_REASON_VALUE && trimmedOtherReason.length < OTHER_REASON_MIN_LENGTH
  const isCancellationFeeNotConfirmed = hasCancellationFee === true && !isCancellationFeeConfirmed
  const isSubmitDisabled =
    hasCancellationFee === null || isOtherReasonTooShort || isCancellationFeeNotConfirmed
  const otherReasonError =
    selectedReason === OTHER_REASON_VALUE && trimmedOtherReason.length > 0 && isOtherReasonTooShort
      ? otherReasonTooShortError()
      : null

  const onCancelHandler = async (id, values) => {
    if (isSubmitDisabled) {
      return
    }
    const reason =
      values.reason_option === OTHER_REASON_VALUE ? trimmedOtherReason : values.reason_option
    try {
      const {
        reservation: canceledReservation,
        flush,
      } = await reservationService.cancelReservation(id, {
        reason,
        cancel_fee: price,
        status: props.reservationStatus,
      })

      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      const reservationPath = `/reservations/${canceledReservation.slug ||
        canceledReservation.id}?cancellation_complete=1`
      window.location.href = reservationPath
    } catch (_error) {
      // Handled by the global axios interceptor.
      console.error(_error)
    }
  }

  React.useEffect(() => {
    const fetchRefundAmount = async () => {
      try {
        const { price: refundPrice } = await reservationService.refundAmountReservation(
          props.reservationId
        )
        setPrice(refundPrice)
      } catch (_error) {
        // Handled by the global axios interceptor.
        console.error(_error)
      }
    }

    fetchRefundAmount()
  }, [props.reservationId])

  return (
    <Wrapper>
      {hasCancellationFee === null ? null : hasCancellationFee ? (
        <div className="cancel-state price">
          <SvgIcon name="info" className="icon" ariaHidden={true} role="presentation" />
          {I18n.t('cancel.cancel_reservation_confirmation', { price: price || 0 })}
        </div>
      ) : (
        <div className="cancel-state">
          <SvgIcon name="check_circle" className="icon" ariaHidden={true} role="presentation" />
          {I18n.t('cancel.cancel_reservation_no_price')}
        </div>
      )}
      <Form
        fields={{
          reason_option: 'reason_option',
          other_reason: 'other_reason',
        }}
        customValidation={(field, value) => {
          if (
            field === 'other_reason' &&
            selectedReason === OTHER_REASON_VALUE &&
            value.trim().length < OTHER_REASON_MIN_LENGTH
          ) {
            return otherReasonTooShortError()
          }

          return null
        }}
        handleSubmit={(initialValues, values) => onCancelHandler(props.reservationId, values)}
      >
        <p className="cancel-reason-prompt">{I18n.t('cancel.cancel_reason_prompt')}</p>
        <InputRadio
          name="reason_option"
          options={CANCEL_REASON_OPTIONS}
          defaultValue={selectedReason}
          onChangeHandler={event => setSelectedReason(event.target.value)}
          required={true}
        />
        {selectedReason === OTHER_REASON_VALUE ? (
          <InputTextArea
            name="other_reason"
            className="other-reason-textarea"
            value={otherReason}
            onChangeHandler={event => setOtherReason(event.target.value)}
            placeholder={I18n.t('cancel.cancel_reason_placeholder')}
            required={true}
            minLength={OTHER_REASON_MIN_LENGTH}
            error={otherReasonError}
          />
        ) : (
          <input type="hidden" name="other_reason" value="" />
        )}
        {hasCancellationFee === null
          ? null
          : hasCancellationFee && (
              <p className="cancel-fee-confirmation-prompt">
                {I18n.t('cancel.cancel_reservation_confirmation', { price: price || 0 })}
              </p>
            )}
        {hasCancellationFee === true ? (
          <div className="cancel-fee-confirmation-checkbox">
            <CheckBox
              name="confirm_cancellation_fee"
              showLabelInline={true}
              label="I understand that cancellation fees will apply"
              checked={isCancellationFeeConfirmed}
              onChangeHandler={event => setIsCancellationFeeConfirmed(event.target.checked)}
            />
          </div>
        ) : null}
        <div className="submit-button">
          <Button primary={true} rounded={true} disabled={isSubmitDisabled}>
            Cancel Reservation
          </Button>
        </div>
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .cancel-reason-prompt {
    margin-bottom: 16px;
    font-weight: 700;
  }
  .InputRadio_Labels {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 12px;
    label {
      gap: 4px;
      font-weight: 500;
    }
  }
  .other-reason-textarea {
    margin-top: 12px;
  }
  .cancel-fee-confirmation-prompt {
    margin-top: 32px;
    font-weight: 700;
    text-align: center;
  }
  .cancel-fee-confirmation-checkbox {
    margin-top: 8px;
    .CheckBox_Main {
      justify-content: center;
      label {
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
  .submit-button {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    .Button {
      width: 280px;
      height: 48px;
      font-size: 14px;
      border-radius: 8px;
    }
  }
`

export default ReservationCancelModalBody
